import type { ComponentType } from "react";
import {
  Carrot,
  UtensilsCrossed,
  AlertCircle,
  Clock,
  Droplets,
  ClipboardList,
  AlertTriangle,
  Activity,
} from "lucide-react";
import type { IconBadgeVariant } from "@/components/ui/IconBadge";

export interface SosTopicFaq {
  q: string;
  a: string;
}

export interface SosTopicAskChip {
  label: string;
  prefill: string;
}

/** Тир, требуемый для доступа к блоку. Блокировка по конфигу, не по индексу. */
export type HelpRequiredTier = "free" | "paid";

export interface SosTopicConfig {
  id: string;
  title: string;
  subtitle: string;
  /** Для заголовка деталки (1 строка) */
  shortSubtitle: string;
  bullets: string[];
  icon: ComponentType<{ className?: string }>;
  /** Вариант tint для IconBadge (плашка слева у карточки). */
  badgeVariant: IconBadgeVariant;
  action: "sos" | "chat";
  prefillText?: string;
  requiresPremium: boolean;
  /** Тир для доступа: paid = только Premium/Trial. Используется вместо индекса. */
  requiredTier: HelpRequiredTier;
  /** Короткий заголовок на главной Help и в sheet (если не задан — `title`). */
  displayTitle?: string;
  chipExamples: string[];
  /** Контент детальной страницы */
  intro: string[];
  checklistNow: string[];
  redFlags: string[];
  faq: SosTopicFaq[];
  askChips: SosTopicAskChip[];
  /** Буллеты ценности Premium (2–4) */
  premiumValue?: string[];
}

const TOPICS: SosTopicConfig[] = [
  {
    id: "new_food",
    title: "Как безопасно ввести новый продукт?",
    subtitle: "Пошаговый ввод прикорма под возраст",
    shortSubtitle: "Пошаговый ввод прикорма",
    bullets: [
      "Порядок и объём первой пробы",
      "Признаки готовности к продукту",
      "Когда отложить и когда к врачу",
    ],
    icon: Carrot,
    badgeVariant: "sage",
    action: "sos",
    displayTitle: "Новый продукт",
    requiresPremium: true,
    requiredTier: "paid",
    chipExamples: [
      "Как вводить яйцо?",
      "Можно ли рыбу в 8 месяцев?",
      "Сколько давать в первый раз?",
    ],
    intro: [
      "Новый продукт вводят по одному, маленькими порциями, с паузой 3–5 дней, чтобы заметить реакцию. Начинают с половинки чайной ложки и постепенно увеличивают объём.",
      "Важно давать продукт в первой половине дня и не вводить несколько новинок одновременно. Если ребёнок отказывается — не настаивайте, предложите снова через несколько дней.",
    ],
    checklistNow: [
      "Выберите один продукт и возрастную норму по рекомендациям педиатра.",
      "Дайте первую пробу утром, в привычное кормление.",
      "Наблюдайте за кожей, стулом и поведением 2–3 дня.",
      "При любых сомнениях — отложите продукт и при необходимости обратитесь к врачу.",
    ],
    redFlags: [
      "Сыпь, отёк губ/лица, затруднённое дыхание после пробы.",
      "Сильная или многократная рвота, кровь в стуле.",
      "Вялость, отказ от еды и питья.",
    ],
    faq: [
      { q: "Сколько давать в первый раз?", a: "Начинают с 1/2 чайной ложки пюре или каши. За 5–7 дней объём доводят до возрастной нормы (по рекомендациям ВОЗ/педиатра)." },
      { q: "Как это связано с планом прикорма в приложении?", a: "В плане три дня пошагового введения одного продукта, затем мягкое окно наблюдения за реакцией в духе общих рекомендаций 3–5 дней. Продукт не считается «освоенным» автоматически только по трём дням — можно отметить, как перенёс малыш." },
      { q: "Как часто вводить новый продукт?", a: "Один новый продукт раз в 3–5 дней. Так проще понять, на что была реакция, если она появится." },
      { q: "Что делать, если ребёнок выплёвывает?", a: "Не заставляйте. Предложите снова через несколько дней, в другом виде (например, пюре вместо кусочка). Иногда нужно 10–15 проб." },
      { q: "Можно ли давать соль и сахар до года?", a: "До 12 месяцев лучше не добавлять соль и сахар. Мёд — не давать до года (риск ботулизма)." },
    ],
    askChips: [
      { label: "Как вводить яйцо?", prefill: "Как безопасно ввести яйцо в прикорм?" },
      { label: "Рыба в 8 месяцев", prefill: "Можно ли давать рыбу в 8 месяцев и как начать?" },
      { label: "Сколько давать в первый раз?", prefill: "Сколько грамм нового продукта давать в первый раз?" },
      { label: "Как понять, что аллергии нет?", prefill: "Как понять, что у ребёнка нет аллергии на новый продукт?" },
      { label: "Порядок ввода овощей", prefill: "В каком порядке лучше вводить овощи?" },
    ],
    premiumValue: [
      "Персональный план ввода прикорма под возраст и аллергии.",
      "Конкретные объёмы и сроки по продуктам.",
      "Рекомендации при отказе или реакции.",
    ],
  },
  {
    id: "allergy",
    title: "Аллергия или реакция — что делать?",
    subtitle: "Разбираем сыпь, красноту и отказ от продукта",
    shortSubtitle: "Сыпь, краснота и когда к врачу",
    bullets: [
      "Отличить аллергию от нормы",
      "Что делать сразу и что отложить",
      "Когда обязательно к врачу",
    ],
    icon: AlertCircle,
    badgeVariant: "blue",
    action: "sos",
    displayTitle: "Аллергия",
    requiresPremium: true,
    requiredTier: "paid",
    chipExamples: [
      "Сыпь после яблока",
      "Покраснели щеки",
      "Зуд после нового продукта",
    ],
    intro: [
      "Реакция на продукт может быть лёгкой (лёгкое покраснение щёк, сыпь на теле) или выраженной (отёк, затруднённое дыхание). Лёгкие проявления часто связаны с количеством продукта или способом введения.",
      "При любой сыпи или красноте после нового продукта его временно исключают и наблюдают. Если симптомы нарастают или есть отёк лица/горла — нужна срочная помощь.",
    ],
    checklistNow: [
      "Исключите продукт, после которого появилась реакция.",
      "Не вводите другие новые продукты 3–5 дней.",
      "Обеспечьте достаточное питьё и не перегревайте ребёнка.",
      "Зафиксируйте: что ел, когда появилась сыпь/краснота, как долго держалась.",
    ],
    redFlags: [
      "Отёк губ, языка, лица или шеи.",
      "Затруднённое дыхание, хрипы, сильная осиплость.",
      "Распространённая сыпь с отёком, резкое ухудшение состояния.",
      "Многократная рвота, вялость после еды.",
    ],
    faq: [
      { q: "Сыпь после яблока — это аллергия?", a: "Может быть реакция на кислоту или на конкретный сорт. Исключите продукт, понаблюдайте. При повторении — обсудите с педиатром или аллергологом." },
      { q: "Через сколько проходит реакция?", a: "Лёгкая сыпь часто сходит за 1–3 дня после исключения продукта. Если дольше или усиливается — к врачу." },
      { q: "Нужно ли сдавать анализы?", a: "Решение за врачом. Часто достаточно ведения дневника питания и пошагового исключения. При повторных реакциях направляют к аллергологу." },
      { q: "Можно ли пробовать продукт снова?", a: "Только по рекомендации врача. Самостоятельно не стоит, особенно при выраженной реакции в прошлый раз." },
    ],
    askChips: [
      { label: "Сыпь после яблока", prefill: "После яблока появилась сыпь у ребёнка. Что делать?" },
      { label: "Покраснели щеки", prefill: "Покраснели щёки после нового продукта. Это аллергия?" },
      { label: "Зуд после продукта", prefill: "Ребёнок чешется после нового продукта. Как поступить?" },
      { label: "Через сколько проходит?", prefill: "Через сколько обычно проходит сыпь после исключения продукта?" },
    ],
    premiumValue: [
      "Индивидуальный разбор рациона с учётом аллергий.",
      "План замен и безопасных альтернатив.",
    ],
  },
  {
    id: "constipation_diarrhea",
    title: "Стул малыша: норма или повод волноваться?",
    subtitle: "Частота, консистенция и когда действовать",
    shortSubtitle: "Частота, консистенция и красные флаги",
    bullets: [
      "Что считается нормой по возрасту",
      "Когда скорректировать питанием",
      "Красные флаги для визита к врачу",
    ],
    icon: Activity,
    badgeVariant: "apricot",
    action: "sos",
    displayTitle: "Стул малыша",
    requiresPremium: true,
    requiredTier: "paid",
    chipExamples: [
      "Зеленый стул 2 дня",
      "Жидкий после прикорма",
      "Раз в 3 дня — нормально?",
    ],
    intro: [
      "Норма стула у детей сильно различается: от нескольких раз в день до раза в 2–3 дня. Важнее консистенция и самочувствие ребёнка, чем строгое число раз.",
      "Зеленоватый стул при прикорме, кашицеобразная консистенция и отсутствие плача при дефекации чаще всего укладываются в норму. Тревожные признаки: кровь, слизь в большом количестве, очень жидкий стул с температурой или вялостью.",
    ],
    checklistNow: [
      "Оцените консистенцию и цвет, запишите частоту за 2–3 дня.",
      "Убедитесь, что ребёнок получает достаточно жидкости.",
      "При запоре — возрастные нормы клетчатки (овощи, фрукты) и движения.",
      "При жидком стуле — не отменяйте грудь/смесь, прикорм по переносимости.",
    ],
    redFlags: [
      "Кровь в стуле (прожилки или больше).",
      "Очень жидкий стул чаще 5–8 раз в сутки + температура или вялость.",
      "Отсутствие стула несколько дней + боль, плач, отказ от еды.",
      "Резкое изменение запаха и цвета с ухудшением состояния.",
    ],
    faq: [
      { q: "Раз в 3 дня — это запор?", a: "Не всегда. Если стул мягкий, ребёнок не тужится и не плачет — часто это вариант нормы. Запор — твёрдый стул, боль при дефекации." },
      { q: "Зеленый стул — нормально?", a: "Часто да: при прикорме, при избытке переднего молока или железа. Если ребёнок бодр и ест нормально — можно наблюдать." },
      { q: "Жидкий стул после прикорма?", a: "Небольшое разжижение при вводе нового продукта бывает. Исключите продукт на несколько дней, следите за частотой и общим состоянием." },
      { q: "С прожилками — что это?", a: "Прожилки крови — повод показаться врачу в ближайшие дни. Не паникуйте, но не откладывайте визит." },
    ],
    askChips: [
      { label: "Зеленый стул 2 дня", prefill: "У ребёнка второй день зеленоватый стул. Это норма?" },
      { label: "Жидкий после прикорма", prefill: "После ввода прикорма стул стал жидким. Что делать?" },
      { label: "Раз в 3 дня — нормально?", prefill: "Стул раз в 3 дня — это норма для грудничка?" },
      { label: "С прожилками", prefill: "В стуле заметили прожилки. Что это может быть?" },
    ],
    premiumValue: [
      "Персональные рекомендации по питанию при запоре/жидком стуле.",
      "План коррекции рациона под возраст.",
    ],
  },
  {
    id: "spitting_up",
    title: "Срыгивания: норма или проблема?",
    subtitle: "Объём, частота и когда нужен врач",
    shortSubtitle: "Объём, частота и когда к врачу",
    bullets: [
      "Физиологическая норма по возрасту",
      "Как уменьшить срыгивания",
      "Когда это уже не норма",
    ],
    icon: Droplets,
    badgeVariant: "apricot",
    action: "sos",
    displayTitle: "Срыгивания",
    requiresPremium: true,
    requiredTier: "paid",
    chipExamples: [
      "Срыгивает после каждого кормления",
      "Немного — это нормально?",
      "Когда это опасно?",
    ],
    intro: [
      "Умеренные срыгивания после кормления у детей до 6–12 месяцев считаются нормой: небольшой объём (до столовой ложки), без примесей, ребёнок прибавляет в весе и ведёт себя как обычно.",
      "Если срыгивания обильные, «фонтаном», или ребёнок беспокоится, плохо набирает вес — нужна консультация педиатра, чтобы исключить рефлюкс и другие причины.",
    ],
    checklistNow: [
      "Держите ребёнка столбиком 10–15 минут после кормления.",
      "Не укладывайте сразу на спину после еды.",
      "Проверьте, не переедает ли ребёнок (объём по возрасту).",
      "При искусственном вскармливании — обсудите с врачом антирефлюксную смесь при необходимости.",
    ],
    redFlags: [
      "Срыгивания «фонтаном», большим объёмом.",
      "Срыгивания с кровью или жёлто-зелёным содержимым.",
      "Плохая прибавка веса, отказ от еды, плач при кормлении.",
      "Затруднённое дыхание или сильное беспокойство после срыгивания.",
    ],
    faq: [
      { q: "Срыгивает после каждого кормления — норма?", a: "Если объём небольшой (до ст. л.) и ребёнок хорошо набирает вес — чаще норма. Если обильно или вес стоит — к врачу." },
      { q: "До какого возраста допустимо?", a: "У многих уменьшается к 6–9 месяцам, у кого-то до года. После года обильные срыгивания — повод к врачу." },
      { q: "Когда это опасно?", a: "Опасно: большой объём, кровь/желчь, плохой набор веса, сильное беспокойство. В этих случаях — к врачу." },
    ],
    askChips: [
      { label: "После каждого кормления", prefill: "Ребёнок срыгивает после каждого кормления. Нормально ли это?" },
      { label: "Немного — нормально?", prefill: "Срыгивает немного после еды. Это норма?" },
      { label: "До какого возраста?", prefill: "До какого возраста срыгивания считаются нормой?" },
      { label: "Когда к врачу?", prefill: "Когда при срыгиваниях нужно обращаться к врачу?" },
    ],
    premiumValue: [
      "Персональные рекомендации по режиму и объёму кормлений.",
      "План действий при частых срыгиваниях.",
    ],
  },
  {
    id: "food_refusal",
    title: "Ребёнок не хочет есть — что делать?",
    subtitle: "Причины и мягкие решения без давления",
    shortSubtitle: "Причины отказа и мягкие решения",
    bullets: [
      "Типичные причины отказа по возрасту",
      "Как вернуть аппетит без стресса",
      "Когда стоит проверить здоровье",
    ],
    icon: UtensilsCrossed,
    badgeVariant: "apricot",
    action: "sos",
    displayTitle: "Не хочет есть",
    requiresPremium: false,
    requiredTier: "free",
    chipExamples: [
      "Отказывается от прикорма",
      "Ест только пюре",
      "Плачет во время кормления",
    ],
    intro: [
      "Отказ от еды бывает из-за прорезывания зубов, болезни, усталости, нелюбимой консистенции или просто «не сегодня». Давление и уговоры обычно ухудшают ситуацию.",
      "Важно сохранять спокойствие, предлагать еду в привычное время, не заменять полноценный приём перекусами и следить за общим состоянием и прибавкой веса. Если отказ длительный и вес не растёт — к врачу.",
    ],
    checklistNow: [
      "Предлагайте еду в спокойной обстановке, без отвлечений и принуждения.",
      "Сохраняйте режим: завтрак, обед, ужин + перекусы по возрасту.",
      "Давайте возможность есть самому (руками, ложкой), даже если грязно.",
      "При длительном отказе — проверьте здоровье (зубы, горло, живот) и при необходимости обратитесь к педиатру.",
    ],
    redFlags: [
      "Резкая потеря веса или долго нет прибавки.",
      "Отказ от питья, вялость, сонливость.",
      "Боль при глотании, плач при виде еды.",
      "Рвота после еды или признаки обезвоживания.",
    ],
    faq: [
      { q: "Отказывается от прикорма — что делать?", a: "Не заставляйте. Предлагайте в разное время, разную консистенцию и форму. Иногда помогает есть вместе с ребёнком и давать пробовать с руки." },
      { q: "Ест только пюре", a: "Постепенно вводите кусочки и мягкие продукты. Давайте безопасную еду в руки. Несколько недель — нормальный срок для перехода." },
      { q: "Плачет во время кормления", a: "Возможны боль (уши, горло, живот) или дискомфорт. Исключите нажим, проверьте здоровье. При необходимости — к врачу." },
    ],
    askChips: [
      { label: "Отказывается от прикорма", prefill: "Ребёнок отказывается от прикорма. Как мягко приучить?" },
      { label: "Ест только пюре", prefill: "Ест только пюре, отказывается от кусочков. Что делать?" },
      { label: "Кидает еду", prefill: "Ребёнок кидает еду и не сидит за столом. Как реагировать?" },
      { label: "Плачет во время кормления", prefill: "Плачет во время кормления. В чём может быть причина?" },
    ],
    premiumValue: [
      "Персональный разбор причин отказа с учётом возраста.",
      "План мягкого ввода продуктов и режима.",
    ],
  },
  {
    id: "routine",
    title: "График кормления: подходит ли возрасту?",
    subtitle: "Режим прикорма и объёмы",
    shortSubtitle: "Режим и объёмы по возрасту",
    bullets: [
      "Сколько приёмов пищи по возрасту",
      "Ночные кормления: норма и отказ",
      "Переход на общий стол",
    ],
    icon: Clock,
    badgeVariant: "sand",
    action: "sos",
    displayTitle: "Режим кормления",
    requiresPremium: true,
    requiredTier: "paid",
    chipExamples: [
      "Сколько раз кормить в 6 месяцев?",
      "Ночные кормления — норма?",
      "Как перейти на 3 приема?",
    ],
    intro: [
      "К 6 месяцам обычно 1–2 прикорма в день, к 9 — 2–3, к году — 3 основных приёма плюс перекусы. Грудное молоко или смесь остаются по потребности.",
      "Ночные кормления у многих сохраняются до года и дольше. Это нормально. Переход на «взрослый» режим лучше делать постепенно, ориентируясь на аппетит и сон ребёнка.",
    ],
    checklistNow: [
      "Сверьте число приёмов пищи с рекомендациями по возрасту (ВОЗ/педиатр).",
      "Не убирайте ночные кормления насильно — при необходимости обсудите с врачом.",
      "Предлагайте завтрак, обед, ужин в примерно одно время для привыкания.",
      "Перекусы — лёгкие, не перед основным приёмом.",
    ],
    redFlags: [
      "Ребёнок ест слишком редко и теряет вес.",
      "Полный отказ от еды и питья несколько приёмов подряд.",
      "Сильная сонливость и отказ от кормлений (возможна болезнь).",
    ],
    faq: [
      { q: "Сколько раз кормить в 6 месяцев?", a: "Обычно 1–2 прикорма в день плюс грудь/смесь по требованию. К 9 месяцам — 2–3 прикорма." },
      { q: "Ночные кормления — норма?", a: "Да, у многих сохраняются до года и дольше. Убирать принудительно не обязательно, если это не мешает семье и ребёнку." },
      { q: "Как перейти на 3 приема?", a: "Постепенно: вводите завтрак, затем обед, затем ужин. Между ними — грудь/смесь или перекус по возрасту." },
      { q: "Нужны ли перекусы?", a: "Да, лёгкие (фрукт, печенье, йогурт) между основными приёмами. Не перед обедом/ужином, чтобы не перебивать аппетит." },
    ],
    askChips: [
      { label: "Сколько раз в 6 месяцев?", prefill: "Сколько раз в день кормить ребёнка в 6 месяцев?" },
      { label: "Ночные кормления", prefill: "Ночные кормления — это норма? До какого возраста?" },
      { label: "Переход на 3 приема", prefill: "Как перейти на 3 приёма пищи в день?" },
      { label: "Нужны ли перекусы?", prefill: "Нужны ли перекусы между основными приёмами?" },
    ],
    premiumValue: [
      "Персональный режим кормлений под возраст и аппетит.",
      "План перехода на общий стол.",
    ],
  },
  {
    id: "food_diary",
    title: "Наша тарелка",
    subtitle: "Анализ рациона и рекомендации",
    shortSubtitle: "Записать и получить совет",
    bullets: [
      "Разбор того, что ел ребёнок",
      "Баланс и чего не хватает",
      "Идеи на следующий день",
    ],
    icon: ClipboardList,
    badgeVariant: "sand",
    action: "sos",
    prefillText: "Составь дневник питания и дай рекомендации",
    displayTitle: "Наша тарелка",
    requiresPremium: true,
    requiredTier: "paid",
    chipExamples: [
      "Ел кабачок — появилась сыпь",
      "После каши болит живот",
      "Ввел новый продукт сегодня",
    ],
    intro: [
      "Наша тарелка помогает увидеть, сколько и что ест ребёнок, как реагирует на новые продукты и чего может не хватать в рационе.",
      "Записывайте приёмы пищи 2–3 дня подряд: что съел, объём, время. Отмечайте реакции (сыпь, стул, беспокойство). С этим можно обратиться к педиатру или нутрициологу за персональными рекомендациями.",
    ],
    checklistNow: [
      "Запишите 2–3 дня: завтрак, обед, ужин, перекусы, грудь/смесь.",
      "Укажите приблизительные объёмы и способ приготовления.",
      "Отметьте любые реакции (сыпь, стул, сон, настроение).",
      "Сформулируйте вопрос: чего не хватает, что изменить, как разнообразить.",
    ],
    redFlags: [
      "Резкое снижение аппетита и веса — к врачу.",
      "Постоянные боли в животе, рвота после еды — к врачу.",
    ],
    faq: [
      { q: "Ел кабачок — появилась сыпь", a: "Исключите кабачок на несколько дней. Если сыпь сойдёт — возможна реакция. При повторении обсудите с педиатром." },
      { q: "После каши болит живот", a: "Попробуйте другую крупу или уменьшите порцию. Если боль повторяется — к врачу, чтобы исключить непереносимость." },
      { q: "Ввел новый продукт сегодня", a: "Наблюдайте 2–3 дня. Не вводите другие новинки. При реакции — исключите продукт и при необходимости обратитесь к врачу." },
    ],
    askChips: [
      { label: "Ел кабачок — сыпь", prefill: "Вчера дала кабачок, сегодня появилась сыпь. Связано ли это?" },
      { label: "После каши болит живот", prefill: "После каши у ребёнка болит живот. Что делать?" },
      { label: "Ввел новый продукт", prefill: "Сегодня ввела новый продукт. На что смотреть в ближайшие дни?" },
      { label: "Отказался от обеда", prefill: "Ребёнок отказался от обеда. Нормально ли это? Чем компенсировать?" },
    ],
    premiumValue: [
      "Разбор дневника и персональные рекомендации по рациону.",
      "План коррекции и идеи блюд под возраст.",
    ],
  },
  {
    id: "urgent_help",
    title: "Когда срочно обращаться к врачу?",
    subtitle: "Симптомы, при которых нужна помощь сейчас",
    shortSubtitle: "Красные флаги и что подготовить",
    bullets: [
      "Кровь, температура, сильная рвота",
      "Аллергическая реакция и отёк",
      "Вялость и отказ от еды и питья",
    ],
    icon: AlertTriangle,
    badgeVariant: "blue",
    action: "sos",
    displayTitle: "Когда срочно к врачу",
    requiresPremium: false,
    requiredTier: "free",
    chipExamples: [
      "Кровь в стуле",
      "Высокая температура и понос",
      "Сильная сыпь и отек",
    ],
    intro: [
      "Некоторые симптомы требуют срочного осмотра или вызова скорой: кровь в стуле или рвоте, высокая температура с сыпью или судорогами, сильная рвота/понос с признаками обезвоживания, отёк лица/горла, затруднённое дыхание, выраженная вялость, отказ от питья.",
      "Заранее подготовьте: возраст, вес, что ел/пил, когда началось, что уже делали, есть ли температура. Это ускорит помощь.",
    ],
    checklistNow: [
      "Оцените состояние: дыхание, сознание, возможность пить.",
      "При угрозе жизни (отёк горла, судороги, не дышит) — сразу вызывайте скорую.",
      "Запишите: когда началось, что ел/пил, температура, лекарства.",
      "При поносе/рвоте — продолжайте поить маленькими порциями (вода, раствор для регидратации).",
    ],
    redFlags: [
      "Кровь в стуле или рвоте.",
      "Высокая температура + сыпь, не бледнеющая при надавливании, или судороги.",
      "Сильная многократная рвота или понос, отказ от питья, вялость (риск обезвоживания).",
      "Отёк лица/губ/горла, затруднённое дыхание, хрипы.",
      "Ребёнок не просыпается, очень вялый, не реагирует как обычно.",
    ],
    faq: [
      { q: "Кровь в стуле", a: "Обратитесь к врачу в тот же день или в скорую при большом количестве. До визита не давайте обезболивающие без назначения. Запишите вид стула и когда заметили." },
      { q: "Высокая температура и понос", a: "Поить понемногу, следить за признаками обезвоживания (сухость губ, мало мочи, вялость). При температуре выше 38,5 по рекомендации врача — жаропонижающее. Если не пьёт или очень вялый — срочно к врачу." },
      { q: "Сильная рвота", a: "Не кормить насильно, поить маленькими глотками. При многократной рвоте и отказе от питья — риск обезвоживания, к врачу или скорой." },
      { q: "Сильная сыпь и отёк", a: "Если отёк лица/горла или сыпь быстро распространяется — возможна тяжёлая аллергия. При затруднённом дыхании — сразу скорую. Без отёка дыхания — к врачу в тот же день." },
    ],
    askChips: [
      { label: "Кровь в стуле", prefill: "Обнаружила кровь в стуле у ребёнка. Что делать?" },
      { label: "Температура и понос", prefill: "У ребёнка высокая температура и понос. Когда срочно к врачу?" },
      { label: "Сильная рвота", prefill: "Ребёнка сильно рвёт. Что делать и когда вызывать врача?" },
      { label: "Сильная сыпь и отёк", prefill: "Появилась сильная сыпь и отёк. Это срочно?" },
    ],
    premiumValue: [
      "Персональный разбор симптомов и план действий.",
      "Рекомендации, что подготовить для визита к врачу.",
    ],
  },
];

/** Порядок тем: Питание (new_food, food_diary) → Малыш (constipation_diarrhea, spitting_up, food_refusal, urgent_help) → Аллергии → Режим. */
export const SOS_TOPIC_ORDER: string[] = [
  "new_food",
  "food_diary",
  "constipation_diarrhea",
  "spitting_up",
  "food_refusal",
  "urgent_help",
  "allergy",
  "routine",
];

/** Бесплатные сценарии Help (остальные темы — Premium). */
export const QUICK_HELP_TOPIC_IDS: string[] = ["food_refusal", "urgent_help"];

export function getSosTopicConfig(id: string): SosTopicConfig | null {
  return TOPICS.find((t) => t.id === id) ?? null;
}

export function getAllSosTopics(): SosTopicConfig[] {
  return [...TOPICS];
}

export function getSosTopicsInOrder(): SosTopicConfig[] {
  const byId = new Map(TOPICS.map((t) => [t.id, t]));
  return SOS_TOPIC_ORDER.map((id) => byId.get(id)).filter(Boolean) as SosTopicConfig[];
}

export function getTopicDisplayTitle(topic: SosTopicConfig): string {
  return topic.displayTitle ?? topic.title;
}

const HELP_SCREEN_FREE_TOPIC_IDS = ["food_refusal", "urgent_help"] as const;
const HELP_SCREEN_PREMIUM_TOPIC_IDS = [
  "new_food",
  "constipation_diarrhea",
  "spitting_up",
  "allergy",
  "routine",
  "food_diary",
] as const;

/** Темы только Premium (справочный список для аналитики/доков). */
export const REGIME_TOPIC_IDS: string[] = [...HELP_SCREEN_PREMIUM_TOPIC_IDS];

/** Две секции главной «Помощь маме»: бесплатные сценарии сверху, затем Premium. */
export function getHelpMonetizationSections(): { title: string; topics: SosTopicConfig[] }[] {
  const byId = new Map(TOPICS.map((t) => [t.id, t]));
  const free = HELP_SCREEN_FREE_TOPIC_IDS.map((id) => byId.get(id)).filter(Boolean) as SosTopicConfig[];
  const premium = HELP_SCREEN_PREMIUM_TOPIC_IDS.map((id) => byId.get(id)).filter(Boolean) as SosTopicConfig[];
  return [
    { title: "Популярные вопросы", topics: free },
    { title: "Разбор ситуаций", topics: premium },
  ];
}

/** Категория темы для фильтра на главной Help. */
export type HelpTopicCategory = "all" | "feeding" | "routine" | "allergy";

const TOPIC_CATEGORY_MAP: Record<string, HelpTopicCategory> = {
  new_food: "feeding",
  allergy: "allergy",
  constipation_diarrhea: "feeding",
  spitting_up: "feeding",
  food_refusal: "feeding",
  routine: "routine",
  food_diary: "routine",
  urgent_help: "feeding",
};

export function getTopicCategory(topicId: string): HelpTopicCategory {
  return TOPIC_CATEGORY_MAP[topicId] ?? "feeding";
}

export const HELP_CATEGORY_LABELS: Record<HelpTopicCategory, string> = {
  all: "Все",
  feeding: "Питание",
  routine: "Режим",
  allergy: "Аллергии",
};

/** Группы для отображения тем на вкладке Help: заголовок секции + список тем. */
export type HelpTopicGroupId = "feeding" | "baby" | "allergy" | "routine";

export const HELP_TOPIC_GROUP_LABELS: Record<HelpTopicGroupId, string> = {
  feeding: "Питание",
  baby: "Малыш",
  allergy: "Аллергии",
  routine: "Режим",
};

const TOPIC_GROUP_MAP: Record<string, HelpTopicGroupId> = {
  new_food: "feeding",
  food_diary: "feeding",
  allergy: "allergy",
  constipation_diarrhea: "baby",
  spitting_up: "baby",
  food_refusal: "baby",
  urgent_help: "baby",
  routine: "routine",
};

export function getTopicGroup(topicId: string): HelpTopicGroupId {
  return TOPIC_GROUP_MAP[topicId] ?? "baby";
}

const GROUP_ORDER: HelpTopicGroupId[] = ["feeding", "baby", "allergy", "routine"];

export function getTopicsGroupedBySection(): { groupId: HelpTopicGroupId; title: string; topics: SosTopicConfig[] }[] {
  const byId = new Map(TOPICS.map((t) => [t.id, t]));
  const byGroup = new Map<HelpTopicGroupId, string[]>();
  for (const id of SOS_TOPIC_ORDER) {
    if (!byId.has(id)) continue;
    const g = getTopicGroup(id);
    if (!byGroup.has(g)) byGroup.set(g, []);
    byGroup.get(g)!.push(id);
  }
  return GROUP_ORDER.map((groupId) => {
    const ids = byGroup.get(groupId) ?? [];
    const topics = ids.map((id) => byId.get(id)).filter(Boolean) as SosTopicConfig[];
    return { groupId, title: HELP_TOPIC_GROUP_LABELS[groupId], topics };
  }).filter((s) => s.topics.length > 0);
}

export function getQuickHelpTopics(): SosTopicConfig[] {
  const byId = new Map(TOPICS.map((t) => [t.id, t]));
  return QUICK_HELP_TOPIC_IDS.map((id) => byId.get(id)).filter(Boolean) as SosTopicConfig[];
}

export function getRegimeTopics(): SosTopicConfig[] {
  const byId = new Map(TOPICS.map((t) => [t.id, t]));
  return REGIME_TOPIC_IDS.map((id) => byId.get(id)).filter(Boolean) as SosTopicConfig[];
}

export function getRecommendedTopicIds(ageMonths: number | null): string[] {
  if (ageMonths == null || ageMonths < 0) {
    return ["food_refusal", "allergy", "routine"];
  }
  if (ageMonths < 9) {
    return ["new_food", "allergy", "constipation_diarrhea"];
  }
  if (ageMonths < 18) {
    return ["food_refusal", "routine", "spitting_up"];
  }
  return ["food_refusal", "food_diary", "urgent_help"];
}

export const QUICK_CHIP_ITEMS: { label: string; prefillText: string }[] = [
  { label: "Ребёнок не ест", prefillText: "Ребёнок не ест, что делать?" },
  { label: "Сыпь после продукта", prefillText: "Сыпь после продукта — это аллергия?" },
  { label: "Срыгивания: норма?", prefillText: "Срыгивания: это нормально?" },
  { label: "Стул: это ок?", prefillText: "Стул малыша: норма или нет?" },
  { label: "График кормления", prefillText: "Подходит ли график кормления возрасту?" },
  { label: "Новый продукт: как начать", prefillText: "Как безопасно ввести новый продукт?" },
  { label: "Когда к врачу срочно", prefillText: "Когда нужно срочно обратиться к врачу?" },
  { label: "Что можно на ужин", prefillText: "Что можно дать на ужин ребёнку?" },
];
